import * as React from "react";
import {
  AccessReportsAdminPageOptionsEnum,
  AccessSummaryEditChartColorsElementEnum,
  EditPrintColumns
} from "@app/access-reports/admin/";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import cx from "classnames";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { getFormGroupByGroupPropertyIdFromTemplate } from "@app/access-reports/shared/utility";
import { FlyoutLink } from "@app/shared/Flyouts";
import { wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { JSX } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons"; 
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { AccessOverviewEditChartColors } from "@app/access-reports/admin/AccessOverviewEditChartColors";
import { AccessSummaryEditChartColors } from "@app/access-reports/admin/AccessSummaryEditChartColors";
import { AccessRadiusSearchPageElementEnum } from "@app/access-reports/shared/accessRadiusSearchPageElementEnum";
import { AccessReportsColumn } from "@app/access-reports/shared/accessReportsColumn";
import { AccessReportsPageOptionsElementEnum } from "@app/access-reports/shared/accessReportsPageOptionsElementEnum";
import { AccessReportsWizardElement } from "@app/access-reports/shared/accessReportsWizardElement";
import { AccessReportsWizardElementTypeEnum } from "@app/access-reports/shared/accessReportsWizardElementTypeEnum";
import { AccessReportsWizardFormGroup } from "@app/access-reports/shared/accessReportsWizardFormGroup";
import { AccessReportsWizardGroupPropertyIdEnum } from "@app/access-reports/shared/accessReportsWizardGroupPropertyIdEnum";
import { AccessReportsWizardOption } from "@app/access-reports/shared/accessReportsWizardOption";
import { AccessReportsWizardPage } from "@app/access-reports/shared/accessReportsWizardPage";
import { AccessReportsWizardProduct } from "@app/access-reports/shared/accessReportsWizardProduct";
import { AccessReportsWizardStepEnum } from "@app/access-reports/shared/accessReportsWizardStepEnum";
import { AccessReportsWizardTemplate } from "@app/access-reports/shared/accessReportsWizardTemplate";
import { ByProductDefault } from "@app/access-reports/shared/byProductDefault";
import { ColumnDefaults } from "@app/access-reports/shared/columnDefaults";
import { PageSelectionElementIdEnum } from "@app/access-reports/shared/pageSelectionElementIdEnum";

interface AccessReportsAdminAddProductPageOptionsProps {
  disableUserActions: boolean;
  invalidPageOptionPageElements: Map<string, string>;
  onAddPageClick: (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum) => void;
  onDisclaimerTextChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onPageOptionPageChange: (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, byProductDefaultId: number, elementId: string, value: string | number | boolean) => void;
  onPageOptionPageToggle: (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, key: AccessReportsAdminPageOptionsEnum) => void;
  onPageOptionToggle: (elementId: string, key: AccessReportsAdminPageOptionsEnum) => void;
  onRemoveFormGroupClick: (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, byProductDefaultId: number) => void;
  onSaveColumnsClick: (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, columnDefaults: ColumnDefaults) => void;
  onSaveAccessOverviewChartColorsClick: (byProductDefault: ByProductDefault) => void;
  onSaveAccessSummaryChartColorsClick: (byProductDefault: ByProductDefault) => void;
  product: AccessReportsWizardProduct;
  template: AccessReportsWizardTemplate;
}

interface OptionsTableData {
  [wafflesKeyColumnName]: string;
  blank: JSX.Element;
  default: JSX.Element;
  hide: JSX.Element;
  title: string;
}

interface PageTableData {
  [wafflesKeyColumnName]: string;
  allowMultiple: JSX.Element;
  blank: JSX.Element;
  default: JSX.Element;
  hide: JSX.Element;
  title: string;
}

const AccessReportsAdminAddProductPageOptions: React.FC<AccessReportsAdminAddProductPageOptionsProps> = (props: AccessReportsAdminAddProductPageOptionsProps) => {
  const [sidePanelVisible, setSidePanelVisible] = React.useState(false);

  const hideSidePanel = (): void => {
    if (sidePanelVisible) {
      setSidePanelVisible(false);
    }
  }

  const onSaveAccessSummaryChartColorsClick = (byProductDefault: ByProductDefault): void => {
    props.onSaveAccessSummaryChartColorsClick(byProductDefault);
    hideSidePanel();
  }

  const onSaveAccessOverviewChartColorsClick = (byProductDefault: ByProductDefault): void => {
    props.onSaveAccessOverviewChartColorsClick(byProductDefault);
    hideSidePanel();
  }

  const onSaveColumnsClick = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, columnDefaults: ColumnDefaults): void => {
    props.onSaveColumnsClick(groupPropertyId, columnDefaults);
    hideSidePanel();
  }

  const buildDisclaimerPageSection = (): React.ReactNode => {
    const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(props.template, AccessReportsWizardStepEnum.PageOptions);
    const legalDisclaimerElement: AccessReportsWizardElement = pageOptionsFormGroup?.elements.find(x => x.elementId === AccessReportsPageOptionsElementEnum.LegalDisclaimer);

    if (!!legalDisclaimerElement?.options) {
      const productOption: AccessReportsWizardOption = legalDisclaimerElement.options.find(x => x.id === props.product.id);

      if (!!productOption) {
        // the disclaimer page text area will remain disabled
        const enableDisclaimerTextarea: boolean = getCheckboxValue(legalDisclaimerElement, AccessReportsAdminPageOptionsEnum.Default);

        return (
          <React.Fragment>
            {buildOptionsTable([legalDisclaimerElement], "Disclaimer Page")}

            <div className="row">
              <div className="col-12">
                <hr className="mt-0" />
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <div className="access-reports-pages-label">
                  <div className="form-group">
                    <QESTextArea
                      label="Legal Disclaimer Text"
                      disabled={!enableDisclaimerTextarea || props.disableUserActions}
                      testId="access-reports-admin__add-product--disclaimer-textarea"
                      maxLength={32768}
                      onChange={(e) => props.onDisclaimerTextChange(e)}
                      value={productOption.name}
                    />
                  </div>
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      }
    }
  }

  const buildPageOptionsSection = (): React.ReactNode => {
    const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(props.template, AccessReportsWizardStepEnum.PageOptions);
    const excludedElements: AccessReportsPageOptionsElementEnum[] = [
      AccessReportsPageOptionsElementEnum.LegalDisclaimer,
      props.product.skipAccessAnalysis && AccessReportsPageOptionsElementEnum.SplitEmployeeGroupByStandard,
      props.product.skipAccessAnalysis && AccessReportsPageOptionsElementEnum.SplitPagesBySpecialty,
    ];
    const filteredPageOptionsFormGroups: AccessReportsWizardElement[] = pageOptionsFormGroup?.elements.filter(x => !excludedElements.includes(x.elementId as AccessReportsPageOptionsElementEnum));

    if (filteredPageOptionsFormGroups.length) {
      return buildOptionsTable(filteredPageOptionsFormGroups);
    }
  }

  const buildOptionsTable = (pageOptions: AccessReportsWizardElement[], title?: string): React.ReactNode => {
    const columnConfig = {
      blank: {
        displayText: "",
        noSort: true,
        testId: "blank",
      },
      default: {
        displayText: "Default",
        noSort: true,
        testId: "default",
      },
      hide: {
        displayText: "Hide",
        noSort: true,
        testId: "hide",
      },
      title: {
        displayText: title ? title : "Option",
        noSort: true,
        testId: "title",
      },
    };

    const data = pageOptions.map((pageOption) => {
      // note: disclaimer and separator pages will always be hidden,
      // therefore, the "hidden" checkbox should always remain disabled
      // and the "default" checkbox should never be disabled
      const defaultCheckbox: boolean = getCheckboxValue(pageOption, AccessReportsAdminPageOptionsEnum.Default);
      const hideCheckbox: boolean = getCheckboxValue(pageOption, AccessReportsAdminPageOptionsEnum.Hide);
      /* oxlint-disable sort-keys */
      return {
        [wafflesKeyColumnName]: pageOption.elementId,
        title: splitKey(pageOption.elementId),
        default: (
          <QESCheckbox
            checked={defaultCheckbox}
            className="m-1"
            disabled={props.disableUserActions || (
              hideCheckbox &&
              pageOption.elementId !== AccessReportsPageOptionsElementEnum.SeparatorPage &&
              pageOption.elementId !== AccessReportsPageOptionsElementEnum.LegalDisclaimer
            )}
            label=""
            testId={`access-reports-admin__add-product--default-${pageOption.elementId}`}
            onChange={() => props.onPageOptionToggle(pageOption.elementId, AccessReportsAdminPageOptionsEnum.Default)}
          />
        ),
        hide: (
          <QESCheckbox
            checked={hideCheckbox}
            className="m-1"
            disabled={props.disableUserActions || (
              defaultCheckbox ||
              pageOption.elementId === AccessReportsPageOptionsElementEnum.SeparatorPage ||
              pageOption.elementId === AccessReportsPageOptionsElementEnum.LegalDisclaimer
            )}
            label=""
            testId={`access-reports-admin__add-product--hide-${pageOption.elementId}`}
            onChange={() => props.onPageOptionToggle(pageOption.elementId, AccessReportsAdminPageOptionsEnum.Hide)}
          />
        ),
        blank: <>&nbsp;</>,
      }
      /* oxlint-enable */
    })
    return (
      <div className="access-reports-wizard-admin-table">
        <Waffles<OptionsTableData>
          columns={columnConfig}
          data={data}
          disableProcessingOverlay={true}
          disableStickyHeader={true}
          fullWidth={true}
          testId={`page-options${title ? `-${title}` : "-option"}`}
        />
      </div>
    );
  }

  const buildPages = (): React.ReactNode => {
    const columnConfig = {
      allowMultiple: {
        displayText: "Allow Multiple",
        noSort: true,
        testId: "allow-multiple",
      },
      blank: {
        displayText: "",
        noSort: true,
        testId: "blank",
      },
      default: {
        displayText: "Default",
        noSort: true,
        testId: "default",
      },
      hide: {
        displayText: "Hide",
        noSort: true,
        testId: "hide",
      },
      title: {
        displayText: "Option",
        noSort: true,
        testId: "title",
      },
    };

    let groupPropertyIds: AccessReportsWizardGroupPropertyIdEnum[] = [];

    if (props.product.skipAccessAnalysis) {
      groupPropertyIds = [AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages];
    } else {
      groupPropertyIds = Object.values(AccessReportsWizardGroupPropertyIdEnum);
    }

    return (
      <React.Fragment>
        {groupPropertyIds.map((groupPropertyId, index) => {
          const pageName: string = getPageName(groupPropertyId);
          const defaultCheckbox: boolean = getPagesCheckboxValue(groupPropertyId, AccessReportsAdminPageOptionsEnum.Default);
          const hideCheckbox: boolean = getPagesCheckboxValue(groupPropertyId, AccessReportsAdminPageOptionsEnum.Hide);
          const allowMultipleCheckbox: boolean = getPagesCheckboxValue(groupPropertyId, AccessReportsAdminPageOptionsEnum.AllowMultiple);

          const pageOptionsPage: AccessReportsWizardPage = props.template.pages.find(x => x.title === AccessReportsWizardStepEnum.PageOptions);
          const pageFormGroup: AccessReportsWizardFormGroup = pageOptionsPage?.formGroups.find(x => x.groupPropertyId === groupPropertyId);
          const byProductDefaults: ByProductDefault[] = pageFormGroup.byProductDefaults.filter(x => x.productId === props.product.id);

          /* oxlint-disable sort-keys */
          const data = [{
            [wafflesKeyColumnName]: pageName,
            title: pageName,
            default: (
              <QESCheckbox
                checked={defaultCheckbox}
                className="m-1"
                disabled={hideCheckbox || props.disableUserActions}
                label=""
                testId={`access-reports-admin__add-product--default-${groupPropertyId}`}
                onChange={() => props.onPageOptionPageToggle(groupPropertyId, AccessReportsAdminPageOptionsEnum.Default)}
              />
            ),
            hide: (
              <QESCheckbox
                checked={hideCheckbox}
                className="m-1"
                disabled={defaultCheckbox || props.disableUserActions}
                label=""
                testId={`access-reports-admin__add-product--hide-${groupPropertyId}`}
                onChange={() => props.onPageOptionPageToggle(groupPropertyId, AccessReportsAdminPageOptionsEnum.Hide)}
              />
            ),
            allowMultiple: (
              <QESCheckbox
                checked={allowMultipleCheckbox}
                className="m-1"
                disabled={hideCheckbox || props.disableUserActions}
                label=""
                testId={`access-reports-admin__add-product--allow-multiple-${groupPropertyId}`}
                onChange={() => props.onPageOptionPageToggle(groupPropertyId, AccessReportsAdminPageOptionsEnum.AllowMultiple)}
              />
            ),
            blank: getSidePanel(groupPropertyId)
          }];
          /* oxlint-enable */

          if (byProductDefaults.length) {
            return (
              <React.Fragment key={index}>
                <div className="row">
                  <div className="col-12">
                    <div className="access-reports-wizard-admin-table">
                      <Waffles<PageTableData>
                        columns={columnConfig}
                        data={data}
                        disableProcessingOverlay={true}
                        disableStickyHeader={true}
                        fullWidth={true}
                        testId={`access-reports-page-options-${groupPropertyId}`}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <hr className="mt-0" />
                  </div>
                </div>
                <React.Fragment>
                  {byProductDefaults.map((byProductDefault, byProductDefaultIndex) => (
                    <React.Fragment key={byProductDefaultIndex}>
                      {buildPageOptionsPageDefaults(byProductDefault, pageName, pageFormGroup, byProductDefaultIndex, hideCheckbox)}
                    </React.Fragment>
                  ))}
                  <div className="row">
                    <div className="offset-md-2 col-md-10">
                      <div className="mb-3">
                        <QESButton
                          className="p-0"
                          qesTestId={`access-reports-admin__add-product--add-another-${groupPropertyId}`}
                          qesType="secondary"
                          onClick={() => props.onAddPageClick(groupPropertyId)}
                          disabled={hideCheckbox || props.disableUserActions}
                        >
                          <QesIcon iconName={QesIcons.Plus} description="Add" className="mb3" /> Add Another {pageName} Page
                        </QESButton>
                      </div>
                    </div>
                  </div>
                </React.Fragment>

              </React.Fragment>
            );
          }
        })}
      </React.Fragment>
    );
  }

  const buildPageOptionsPageDefaults = (
    byProductDefault: ByProductDefault,
    pageName: string,
    formGroup: AccessReportsWizardFormGroup,
    formGroupIndex: number,
    disabled: boolean,
  ): React.ReactNode => {
    let elements: AccessReportsWizardElement[] = [];

    switch (formGroup.groupPropertyId) {
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages:
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardDetailsPages:
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage:
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages:
      case AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages:
        elements = formGroup.elements.filter(
          x => x.elementType === AccessReportsWizardElementTypeEnum.Dropdown && !x.hidden
        );
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
        elements = formGroup.elements.filter(
          x => x.elementType === AccessReportsWizardElementTypeEnum.Dropdown || x.elementType === AccessReportsWizardElementTypeEnum.Input
        );
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
        elements = formGroup.elements.filter(
          x => x.elementId !== AccessRadiusSearchPageElementEnum.AccessRadiusSearch && x.elementType !== AccessReportsWizardElementTypeEnum.Unknown
        );
        break;
      default:
        break;
    }

    return (
      elements.length > 0 ? (
        <div className="row align-items-center">
          <div className="col-md-2 px-2">
            {formGroupIndex === 0 &&
              <label
                className={cx({
                  "access-reports-pages-label": true,
                  "label-s": true,
                  "text-muted": disabled,
                })}
              >
                Set {pageName}
              </label>
            }
          </div>
          <div className="col-md-9">
            <div className="row">
              {elements.map((element, index) => (
                <div className="col-md-4" key={index}>
                  {getPageOptionsPageElement(byProductDefault, element, formGroup, formGroupIndex, disabled)}
                </div>
              ))}
            </div>
          </div>

          {formGroupIndex !== 0 && (
            <div className="col-1">
              {!props.disableUserActions ? (
                <QESButton
                  qesType="secondary-danger"
                  qesTestId={`access-reports-admin__add-product--remove-form-group-${formGroupIndex}`}
                  id={`access-reports-admin__add-product--remove-form-group-${formGroupIndex}`}
                  className="text-danger me-3 cursor-pointer demibold p-0"
                  onClick={() => props.onRemoveFormGroupClick(formGroup.groupPropertyId, byProductDefault.id)}
                >
                  Remove
                </QESButton>
              ) : (
                <span
                  className="text-muted me-3 cursor-pointer"
                >
                  Remove
                </span>
              )}

            </div>
          )}
        </div>
      ) : (
        <div className="row">
          <div className="col-12">
            <p>
              <QesIcon iconName={QesIcons.AlertHexagon} description="Error" className="text-danger me-1 mb3" />
              Error - There are no elements to display
            </p>
          </div>
        </div>
      )
    );
  }

  const getCheckboxValue = (pageOptionElement: AccessReportsWizardElement, key: AccessReportsAdminPageOptionsEnum): boolean => {
    const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(props.template, AccessReportsWizardStepEnum.PageOptions);
    const pageOption: AccessReportsWizardElement = pageOptionsFormGroup.elements.find(x => x.elementId === pageOptionElement.elementId);
    let checkboxValue = false;

    const pageOptionProductOption: AccessReportsWizardOption = pageOption.options?.find(x => x.id === props.product.id);

    if (!!pageOptionProductOption) {
      switch (key) {
        case AccessReportsAdminPageOptionsEnum.Default:
          if (pageOptionProductOption.defaultProducts?.includes(props.product.id)) {
            checkboxValue = true;
          }

          break;
        case AccessReportsAdminPageOptionsEnum.Hide:
          if (pageOptionProductOption.hiddenProducts?.includes(props.product.id)) {
            checkboxValue = true;
          }

          break;
        default:
          break;
      }
    }

    return checkboxValue;
  }

  const getPageName = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum): string => {
    switch (groupPropertyId) {
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardDetailsPages:
        return "Access Detail";
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages:
        return "Access Summary";
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage:
        return "Employee Count";
      case AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages:
        return "Access Overview";
      case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
        return "Access Listing";
      case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
        return "Access Radius Search";
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages:
        return "Provider Count";
      default:
        return "Invalid Group Property Id";
    }
  }

  const getPageOptionPageDropdownOptions = (
    element: AccessReportsWizardElement,
    formGroup: AccessReportsWizardFormGroup,
  ): AccessReportsWizardOption[] => {
    let options: AccessReportsWizardOption[] = [];

    if (element.previousRequiredElementId && element.usePreviousElementFilterId && formGroup) {
      const previousRequiredElement = formGroup.elements.find((x) => x.elementId === element.previousRequiredElementId);

      if (previousRequiredElement?.previousRequiredValueId !== null && previousRequiredElement?.previousRequiredValueId !== undefined) {
        options = element.options.filter((x) => (
          (x.previousElementFilterId !== null && x.previousElementFilterId !== undefined) &&
          (x.previousElementFilterId.toString() === previousRequiredElement.previousRequiredValueId.toString()))
        );
      }
    } else {
      options = element.options;
    }

    return options;
  }

  const getPagesCheckboxValue = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, key: AccessReportsAdminPageOptionsEnum): boolean => {
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(props.template, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

    if (!!pageFormGroup) {
      const firstByProductDefault: ByProductDefault = pageFormGroup.byProductDefaults.find(x => x.productId === props.product.id);

      switch (key) {
        case AccessReportsAdminPageOptionsEnum.Default:
          let defaulted = false;

          for (const property of Object.keys(firstByProductDefault)) {
            if (Object.values(PageSelectionElementIdEnum).includes(property as PageSelectionElementIdEnum)) {
              defaulted = firstByProductDefault[property] as boolean;
              break;
            }
          }

          return defaulted;
        case AccessReportsAdminPageOptionsEnum.Hide:
          if (pageFormGroup.hiddenProducts.includes(props.product.id)) {
            return true;
          } else {
            return false;
          }
        case AccessReportsAdminPageOptionsEnum.AllowMultiple:
          if (pageFormGroup.disallowedMultipleProducts.includes(props.product.id)) {
            return false;
          } else {
            return true;
          }
        default:
          break;
      }
    }

    return false;
  }

  const getPageOptionsPageElement = (
    byProductDefault: ByProductDefault,
    element: AccessReportsWizardElement,
    formGroup: AccessReportsWizardFormGroup,
    formGroupIndex: number,
    disabled: boolean,
  ): JSX.Element => {
    switch (element.elementType) {
      case AccessReportsWizardElementTypeEnum.Checkbox:
        return (
          <div className="d-flex align-items-center w-100 h-100">
            <QESCheckbox
              checked={byProductDefault[element.elementId] as boolean}
              disabled={disabled || props.disableUserActions}
              label={element.title}
              testId={`access-reports-admin__add-product--${element.elementId}-${formGroupIndex}`}
              onChange={() => props.onPageOptionPageChange(formGroup.groupPropertyId, byProductDefault.id, element.elementId, !byProductDefault[element.elementId])}
            />
          </div>
        );
      case AccessReportsWizardElementTypeEnum.Dropdown:
        return (
          <QESSelect
            allowEmpty={true}
            containerClassName="mb-3"
            disabled={disabled || props.disableUserActions}
            testId={`access-reports-admin__add-product--${element.elementId}-${formGroupIndex}`}
            hideLabel={!element.title}
            label={element.title}
            onChange={(e) => props.onPageOptionPageChange(formGroup.groupPropertyId, byProductDefault.id, element.elementId, e?.target?.value)}
            value={(byProductDefault[element.elementId])?.toString() || ""}
          >
            {getPageOptionPageDropdownOptions(element, formGroup)?.map((option, optionIndex) => (
              <option
                key={optionIndex}
                value={option.id}
              >
                {option.displayName ? option.displayName : option.name}
              </option>
            ))}
          </QESSelect>
        );
      case AccessReportsWizardElementTypeEnum.Input:
        const errorMessage: string = props.invalidPageOptionPageElements.get(formGroup.id + element.elementId);

        return (
          <QESTextInput
            containerClassName="mb-3"
            testId={`access-reports-admin__add-product--${element.elementId}-${formGroupIndex}`}
            disabled={disabled || props.disableUserActions}
            errorText={errorMessage}
            isValid={!errorMessage}
            unrequiredValidation={true}
            label={element.title}
            onChange={(e) => onPageOptionsPageDefaultInputChange(byProductDefault.id, formGroup.groupPropertyId, element.elementId, e)}
            onBlur={() => onPageOptionsPageDefaultInputChange(byProductDefault.id, formGroup.groupPropertyId, element.elementId, null, byProductDefault[element.elementId] as string)}
            value={byProductDefault[element.elementId] as string | number || ""}
          />
        );
      default:
        break;
    }
  }

  const getSidePanel = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum): JSX.Element => {
    let content: React.ReactNode | (() => React.ReactNode) = null;
    let title = "";

    switch (groupPropertyId) {
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages:
        return (
          <div className="float-end">
            <FlyoutLink
              classNames="qes-button-secondary p-0"
              content={{
                body: renderAccessSummaryEditChartColorsSidePanel(),
                title: "Edit Chart Colors"
              }}
              panelId={`access-reports-admin__add-product--${groupPropertyId}-side-panel-1`}
              size="40"
              testId={`access-reports-admin__add-product--${groupPropertyId}-side-panel-link-1`}
            >
              Edit Chart Colors
            </FlyoutLink>
            <span className="mx-3">
              |
            </span>
            <FlyoutLink
              classNames="qes-button-secondary p-0"
              content={{
                body: renderEditPrintColumnsSidePanel(groupPropertyId) as JSX.Element,
                title: "Edit Print Columns",
              }}
              panelId={`access-reports-admin__add-product--${groupPropertyId}-side-panel-2`}
              size="20"
              testId={`access-reports-admin__add-product--${groupPropertyId}-side-panel-link-2`}
            >
              Edit Print Columns
            </FlyoutLink>
          </div>
        );
      case AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages:
        content = renderAccessOverviewEditChartColorsSidePanel();
        title = "Edit Chart Colors";
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardDetailsPages:
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage:
      case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
      case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages:
        content = renderEditPrintColumnsSidePanel(groupPropertyId);
        title = "Edit Print Columns";
        break;
      default:
        break;
    }

    return (
      <FlyoutLink
        classNames="qes-button-secondary float-end p-0"
        content={{
          body: content as JSX.Element,
          title: title,
        }}
        panelId={`access-reports-admin__add-product--${groupPropertyId}-side-panel`}
        isVisible={sidePanelVisible}
        size="40"
        testId={`access-reports-admin__add-product--${groupPropertyId}-side-panel-link`}
      >
        {title}
      </FlyoutLink>
    );
  }

  const onAccessListingInputChange = (
    byProductDefaultId: number,
    groupPropertyId: AccessReportsWizardGroupPropertyIdEnum,
    elementId: string,
    event?: React.ChangeEvent<HTMLInputElement>,
    value?: string,
  ) => {
    if (!!event) {
      // on change
      const unparsedValue: string = event.target.value;
      let value: number = parseFloat(event.target.value);

      if (elementId.toUpperCase().includes("MILE")) {
        const re = /^([0-9]{0,4}|[0-9]{0,4}\.[0-9]?)$/;

        if (re.test(unparsedValue)) {
          if (value >= 1) {
            // remove leading zeros
            value = parseFloat(unparsedValue.replace(/^0+/, ""));
          }

          if ((!isNaN(value) && value >= 1 && value <= 9999.9) || !unparsedValue || unparsedValue === ".") {
            props.onPageOptionPageChange(groupPropertyId, byProductDefaultId, elementId, (!unparsedValue || unparsedValue === ".") ? unparsedValue : value);
          }
        }
      } else {
        let minValue = 0;
        let maxValue = 999;
        if (elementId.toUpperCase().includes("MINUTE")) {
          minValue = 1;
          maxValue = 9999;
        }

        if ((!isNaN(value) && value >= minValue && value <= maxValue) || !unparsedValue) {
          props.onPageOptionPageChange(groupPropertyId, byProductDefaultId, elementId, !unparsedValue ? unparsedValue : value);
        }
      }
    } else {
      if (elementId.toUpperCase().includes("COUNT")) {
        if (!value || value === "") {
          props.onPageOptionPageChange(groupPropertyId, byProductDefaultId, elementId, "0");
        }
      } else {
        if (!value || value === "" || value === "0" || value === "." || value === "0.") {
          props.onPageOptionPageChange(groupPropertyId, byProductDefaultId, elementId, "1");
        }
      }
    }
  }

  const onAccessRadiusSearchPageChange = (
    byProductDefaultId: number,
    groupPropertyId: AccessReportsWizardGroupPropertyIdEnum,
    elementId: string,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const unparsedValue: string = event.target.value;
    let value: number = parseFloat(event.target.value);
    const re = /^([0-9]{0,4}|[0-9]{0,4}\.[0-9]?)$/;

    if (re.test(unparsedValue)) {
      if (value >= 1) {
        // remove leading zeros
        value = parseFloat(unparsedValue.replace(/^0+/, ""));
      }

      if ((!isNaN(value) && value >= 1 && value <= 999.9) || !unparsedValue) {
        props.onPageOptionPageChange(groupPropertyId, byProductDefaultId, elementId, (!unparsedValue || unparsedValue === ".") ? unparsedValue : value);
      }
    }
  }

  const onPageOptionsPageDefaultInputChange = (
    byProductDefaultId: number,
    groupPropertyId: AccessReportsWizardGroupPropertyIdEnum,
    elementId: string,
    event?: React.ChangeEvent<HTMLInputElement>,
    value?: string,
  ): void => {
    switch (groupPropertyId) {
      case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
        onAccessListingInputChange(byProductDefaultId, groupPropertyId, elementId, event, value);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
        if (!!event) {
          onAccessRadiusSearchPageChange(byProductDefaultId, groupPropertyId, elementId, event);
        }

        break;
      default:
        break;
    }
  }

  const renderAccessSummaryEditChartColorsSidePanel = (): JSX.Element => {
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(props.template, AccessReportsWizardStepEnum.PageOptions, AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages);
    const byProductDefault: ByProductDefault = pageFormGroup.byProductDefaults.find(x => x.productId === props.product.id);

    if (!!pageFormGroup) {
      return (
        <AccessSummaryEditChartColors
          byProductDefault={byProductDefault}
          disableButtons={props.disableUserActions}
          elements={pageFormGroup.elements.filter(x => Object.values(AccessSummaryEditChartColorsElementEnum).includes(x.elementId as AccessSummaryEditChartColorsElementEnum))}
          hideSidePanel={hideSidePanel}
          onSaveAccessSummaryChartColorsClick={onSaveAccessSummaryChartColorsClick}
        />
      );
    }

    return (
      <div className="form-group">
        <QesIcon iconName={QesIcons.AlertHexagon} description="Error" className="text-danger me-1 mb3" />
        <span className="text-muted">
          Error
        </span>
      </div>
    );
  }

  const renderAccessOverviewEditChartColorsSidePanel = (): React.ReactNode => {
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(props.template, AccessReportsWizardStepEnum.PageOptions, AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages);
    const byProductDefault: ByProductDefault = pageFormGroup.byProductDefaults.find(x => x.productId === props.product.id);

    if (!!pageFormGroup) {
      return (
        <AccessOverviewEditChartColors
          byProductDefault={byProductDefault}
          disableButtons={props.disableUserActions}
          elements={pageFormGroup.elements}
          hideSidePanel={hideSidePanel}
          onSaveAccessOverviewChartColorsClick={onSaveAccessOverviewChartColorsClick}
        />
      );
    }

    return (
      <div className="form-group">
        <QesIcon iconName={QesIcons.AlertHexagon} description="Error" className="text-danger me-1 mb3" />
        <span className="text-muted">
          Error
        </span>
      </div>
    );
  }

  const renderEditPrintColumnsSidePanel = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum): React.ReactNode => {
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(props.template, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

    if (!!pageFormGroup) {
      const columnsElement: AccessReportsWizardElement = pageFormGroup.elements.find(x => x.elementId === "columns");
      const accessFilterElement: AccessReportsWizardElement = pageFormGroup.elements.find(x => x.elementId === "accessFilter");

      let accessFilters: AccessReportsWizardOption[] = [];
      if (groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage && groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages) {
        if (!!accessFilterElement?.options) {
          accessFilters = accessFilterElement.options;
        }
      }

      const columnDefaults: ColumnDefaults = pageFormGroup.columnDefaults.find(x => x.productId === props.product.id);

      if (!!columnsElement?.options) {
        return (
          <EditPrintColumns
            accessFilters={accessFilters}
            columnDefaults={columnDefaults}
            columns={columnsElement.options as AccessReportsColumn[]}
            disableButtons={props.disableUserActions}
            groupPropertyId={groupPropertyId}
            hideSidePanel={hideSidePanel}
            onSaveColumnsClick={onSaveColumnsClick}
          />
        );
      }
    }

    return (
      <div className="form-group">
        <QesIcon iconName={QesIcons.AlertHexagon} description="Error" className="text-danger me-1 mb3" />
        <span className="text-muted">
          Error
        </span>
      </div>
    );
  }

  const splitKey = (key: string): string => {
    const splitKey: string = key.replace(/([A-Z])/g, " $1");
    const formattedKey: string = (splitKey.charAt(0).toUpperCase() + splitKey.slice(1));

    return formattedKey;
  }

  if (!!props.template) {
    return (
      <React.Fragment>
        {buildPageOptionsSection()}
        {buildDisclaimerPageSection()}
        {buildPages()}
      </React.Fragment>
    );
  } else {
    return (
      <div className="row">
        <div className="col-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }
}

export { AccessReportsAdminAddProductPageOptions };
